import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { epochToView, HttpGetRequestCacheService } from '@tokeet-frontend/tv3-platform'
import { Billing, BillingChargesPage, BillingChargesPageDto, BillingDto } from './billing.model'
import { isEmpty } from 'lodash'

@Injectable()
export class BillingService {
  constructor(private http: HttpClient, private cache: HttpGetRequestCacheService) {}

  setupIntent() {
    const url = `@api/subscribe/setup`

    return this.http.get<{ client_secret: string }>(url)
  }

  charges(product?: string, refresh = false): Observable<Billing[]> {
    let url = `@api/subscribe/charges`
    if (product) {
      url += `?product=${product}`
    }

    return this.cache
      .cacheGetRequest(url, { refresh })
      .pipe(map((response: BillingDto[]) => this.formatBillings(response)))
  }

  chargesPage({
    product,
    limit = 10,
    startingAfter,
    endingBefore,
    refresh = false,
  }: {
    product?: string
    limit?: number
    startingAfter?: string
    endingBefore?: string
    refresh?: boolean
  }): Observable<BillingChargesPage> {
    let url = `@api/subscribe/charges/paged`
    const query: string[] = []

    if (product) {
      query.push(`product=${product}`)
    }
    if (limit) {
      query.push(`limit=${limit}`)
    }
    if (startingAfter) {
      query.push(`starting_after=${startingAfter}`)
    }
    if (endingBefore) {
      query.push(`ending_before=${endingBefore}`)
    }
    if (query.length > 0) {
      url += `?${query.join('&')}`
    }

    return this.cache.cacheGetRequest(url, { refresh }).pipe(
      map((response: BillingChargesPageDto) => ({
        data: this.formatBillings(response?.data || []),
        hasMore: !!response?.has_more,
        nextCursor: response?.next_cursor,
        prevCursor: response?.prev_cursor,
      }))
    )
  }

  private formatBillings(billings: BillingDto[]): Billing[] {
    return (billings || [])
      .filter((billing) => billing?.id)
      .map((billing) => {
        const name = (billing.card?.name + '').replace(/\:/g, ' ')
        return {
          ...billing,
          createdView: epochToView(billing.created),
          amountView: billing.refunded ? -1 * (billing.amount / 100) : billing.amount / 100,
          cardView: isEmpty(billing.card)
            ? ''
            : `${name} ` +
              $localize`ending in` +
              ` ${billing.card.last4} ` +
              $localize`expiring on` +
              ` ${billing.card.exp_month}/${billing.card.exp_year}`,
          statusView:
            $localize`Status` +
            ` (${billing.status}), ` +
            $localize`Captured` +
            ` (${billing.captured}), ` +
            $localize`Paid` +
            ` (${billing.paid}), ` +
            $localize`Refunded` +
            ` (${billing.refunded})`,
        } as Billing
      })
  }
}
